import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import type { AccountContact, AccountLocation } from "@breeze/shared";
import {
  createAccountContact,
  createAccountLocation,
  createAccountVisit,
  fetchAccountContacts,
  fetchAccountLocations,
} from "../api.js";
import { pcaFind, pcaRetrieve } from "../pca.js";
import type { PcaFindItem } from "../pca.js";

type Props = {
  open: boolean;
  onClose: () => void;
  aid: number;
  // The card this flow is rooted in. Used to invalidate its cluster query
  // after a successful create so the Visits tab refreshes immediately.
  cardId: number;
  accountLabel: string;
  // Called with the new visit's vid so the caller can open it in QuoteEditor.
  onCreated: (newVid: number) => void;
};

type Mode = "pick" | "new";

export function NewQuoteModal({
  open,
  onClose,
  aid,
  cardId,
  accountLabel,
  onCreated,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [locations, setLocations] = useState<AccountLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [contactMode, setContactMode] = useState<Mode>("pick");
  const [locationMode, setLocationMode] = useState<Mode>("pick");

  const [selectedCid, setSelectedCid] = useState<number | null>(null);
  const [selectedLid, setSelectedLid] = useState<number | null>(null);

  // Inline new-contact form fields. Phone is split into Mobile/Home/Work/
  // Other (mirrors order-form.php) so the contact record carries the same
  // shape as legacy-created records and outbound SMS/WA can pick the right
  // line at send time.
  const [cFirst, setCFirst] = useState("");
  const [cLast, setCLast] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhoneMobile, setCPhoneMobile] = useState("");
  const [cPhoneHome, setCPhoneHome] = useState("");
  const [cPhoneWork, setCPhoneWork] = useState("");
  const [cPhoneOther, setCPhoneOther] = useState("");

  // Inline new-location form fields
  const [lAddr1, setLAddr1] = useState("");
  const [lAddr2, setLAddr2] = useState("");
  const [lAddr3, setLAddr3] = useState("");
  const [lCity, setLCity] = useState("");
  const [lPostcode, setLPostcode] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load contacts + locations when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchAccountContacts(aid), fetchAccountLocations(aid)])
      .then(([cs, ls]) => {
        if (cancelled) return;
        setContacts(cs);
        setLocations(ls);
        // Auto-select the first / default.
        if (cs.length > 0 && cs[0]) setSelectedCid(cs[0].cid);
        if (ls.length > 0 && ls[0]) setSelectedLid(ls[0].lid);
        // If the account has no contacts/locations, force "new" mode.
        if (cs.length === 0) setContactMode("new");
        if (ls.length === 0) setLocationMode("new");
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "load failed");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, aid]);

  // Reset all fields on close so reopening gives a clean slate.
  useEffect(() => {
    if (!open) {
      setContacts([]);
      setLocations([]);
      setSelectedCid(null);
      setSelectedLid(null);
      setContactMode("pick");
      setLocationMode("pick");
      setCFirst("");
      setCLast("");
      setCEmail("");
      setCPhoneMobile("");
      setCPhoneHome("");
      setCPhoneWork("");
      setCPhoneOther("");
      setLAddr1("");
      setLAddr2("");
      setLAddr3("");
      setLCity("");
      setLPostcode("");
      setSubmitError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    const contactOk =
      contactMode === "pick"
        ? selectedCid !== null
        : cFirst.trim().length > 0;
    const locationOk =
      locationMode === "pick"
        ? selectedLid !== null
        : lPostcode.trim().length > 0 || lAddr1.trim().length > 0;
    return contactOk && locationOk;
  }, [
    submitting,
    contactMode,
    selectedCid,
    cFirst,
    locationMode,
    selectedLid,
    lPostcode,
    lAddr1,
  ]);

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Resolve cid: either the picked one, or a freshly-created one.
      let cid = selectedCid;
      if (contactMode === "new") {
        const created = await createAccountContact(aid, {
          firstName: cFirst.trim(),
          lastName: cLast.trim(),
          email: cEmail.trim(),
          // `phone` retained for back-compat — server treats it as mobile
          // when phoneMobile is unset.
          phone: cPhoneMobile.trim(),
          phoneMobile: cPhoneMobile.trim(),
          phoneHome: cPhoneHome.trim(),
          phoneWork: cPhoneWork.trim(),
          phoneOther: cPhoneOther.trim(),
        });
        cid = created.cid;
      }
      // Same for lid.
      let lid = selectedLid;
      if (locationMode === "new") {
        const created = await createAccountLocation(aid, {
          address1: lAddr1.trim(),
          address2: lAddr2.trim(),
          address3: lAddr3.trim(),
          city: lCity.trim(),
          postcode: lPostcode.trim(),
        });
        lid = created.lid;
      }
      if (cid === null || lid === null) {
        throw new Error("missing contact or location");
      }
      const res = await createAccountVisit(aid, { cid, lid });
      // Refresh the cluster so the Visits tab picks up the new visit
      // immediately. The server also fires card.updated over SSE so the
      // board itself updates (last-visit postcode + price).
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      onCreated(res.newVid);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[80] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="newq"
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-2xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-50">
                    New Quote
                  </div>
                  <h3 className="mt-1 text-xl leading-tight text-ink-50">
                    {accountLabel}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="space-y-4 px-5 py-4 text-[13px]">
                {loading ? (
                  <div className="rounded-md border border-ink-700 bg-ink-900/40 px-3 py-6 text-center text-ink-200">
                    loading…
                  </div>
                ) : loadError ? (
                  <div className="rounded-md border border-slack-pink/40 bg-slack-pink/10 px-3 py-2 text-slack-pink">
                    {loadError}
                  </div>
                ) : (
                  <>
                    {/* Contact picker */}
                    <section>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                          Service contact
                        </span>
                        <ModeToggle mode={contactMode} setMode={setContactMode} />
                      </div>
                      {contactMode === "pick" ? (
                        contacts.length === 0 ? (
                          <div className="rounded-md border border-dashed border-ink-500 px-3 py-3 text-ink-200">
                            No contacts on this account — add one below.
                          </div>
                        ) : (
                          <select
                            value={selectedCid ?? ""}
                            onChange={(e) => setSelectedCid(Number(e.target.value))}
                            className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                          >
                            {contacts.map((c) => (
                              <option key={c.cid} value={c.cid}>
                                {formatContact(c)}
                              </option>
                            ))}
                          </select>
                        )
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <LabeledInput label="First" value={cFirst} onChange={setCFirst} />
                          <LabeledInput label="Last" value={cLast} onChange={setCLast} />
                          <LabeledInput label="Email" value={cEmail} onChange={setCEmail} />
                          <LabeledInput
                            label="Phone Mobile"
                            value={cPhoneMobile}
                            onChange={setCPhoneMobile}
                          />
                          <LabeledInput
                            label="Phone Home"
                            value={cPhoneHome}
                            onChange={setCPhoneHome}
                          />
                          <LabeledInput
                            label="Phone Work"
                            value={cPhoneWork}
                            onChange={setCPhoneWork}
                          />
                          <LabeledInput
                            label="Phone Other"
                            value={cPhoneOther}
                            onChange={setCPhoneOther}
                          />
                        </div>
                      )}
                    </section>

                    {/* Location picker */}
                    <section>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                          Service location
                        </span>
                        <ModeToggle mode={locationMode} setMode={setLocationMode} />
                      </div>
                      {locationMode === "pick" ? (
                        locations.length === 0 ? (
                          <div className="rounded-md border border-dashed border-ink-500 px-3 py-3 text-ink-200">
                            No locations on this account — add one below.
                          </div>
                        ) : (
                          <select
                            value={selectedLid ?? ""}
                            onChange={(e) => setSelectedLid(Number(e.target.value))}
                            className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                          >
                            {locations.map((l) => (
                              <option key={l.lid} value={l.lid}>
                                {formatLocation(l)}
                              </option>
                            ))}
                          </select>
                        )
                      ) : (
                        <NewLocationForm
                          address1={lAddr1}
                          address2={lAddr2}
                          address3={lAddr3}
                          city={lCity}
                          postcode={lPostcode}
                          onAddress1={setLAddr1}
                          onAddress2={setLAddr2}
                          onAddress3={setLAddr3}
                          onCity={setLCity}
                          onPostcode={setLPostcode}
                        />
                      )}
                    </section>
                  </>
                )}
              </div>

              <footer className="flex items-center justify-between gap-2 border-t border-ink-700 px-5 py-3">
                <div className="text-[13px]">
                  {submitError ? (
                    <span className="text-slack-pink">⚠ {submitError}</span>
                  ) : (
                    <span className="text-ink-200">
                      Creates a blank quote on this account. Opens in the editor.
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {submitting ? "Creating…" : "Create quote"}
                  </button>
                </div>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function ModeToggle({
  mode,
  setMode,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
}): JSX.Element {
  return (
    <div className="flex overflow-hidden rounded-md border border-ink-500 bg-ink-800 text-[12px]">
      <button
        onClick={() => setMode("pick")}
        className={`px-2 py-0.5 uppercase tracking-wider ${
          mode === "pick"
            ? "bg-slack-aubergine text-ink-50"
            : "text-ink-50 hover:bg-ink-700"
        }`}
      >
        Existing
      </button>
      <button
        onClick={() => setMode("new")}
        className={`px-2 py-0.5 uppercase tracking-wider ${
          mode === "new"
            ? "bg-slack-aubergine text-ink-50"
            : "text-ink-50 hover:bg-ink-700"
        }`}
      >
        + New
      </button>
    </div>
  );
}

// Inline form for creating a new service location. Mirrors the PCA flow in
// QuoteEditor's LocationSection: staff enters a postcode (or partial
// address), clicks Find, picks from the results, and we pre-fill the four
// address fields. They can still edit freely afterwards.
function NewLocationForm({
  address1,
  address2,
  address3,
  city,
  postcode,
  onAddress1,
  onAddress2,
  onAddress3,
  onCity,
  onPostcode,
}: {
  address1: string;
  address2: string;
  address3: string;
  city: string;
  postcode: string;
  onAddress1: (v: string) => void;
  onAddress2: (v: string) => void;
  onAddress3: (v: string) => void;
  onCity: (v: string) => void;
  onPostcode: (v: string) => void;
}): JSX.Element {
  const [findState, setFindState] = useState<"idle" | "loading" | "error">("idle");
  const [findResults, setFindResults] = useState<PcaFindItem[]>([]);
  const [findError, setFindError] = useState<string | null>(null);

  async function runFind(): Promise<void> {
    const term = postcode.trim() || [address1, city].filter(Boolean).join(" ").trim();
    if (!term) {
      setFindError("Enter a postcode or address first");
      setFindState("error");
      return;
    }
    setFindState("loading");
    setFindError(null);
    try {
      const items = await pcaFind(term);
      setFindResults(items);
      setFindState("idle");
      if (items.length === 0) setFindError("No addresses found");
    } catch (err) {
      setFindError(err instanceof Error ? err.message : "lookup failed");
      setFindState("error");
    }
  }

  async function pickResult(id: string): Promise<void> {
    try {
      const a = await pcaRetrieve(id);
      if (!a) {
        setFindError("Failed to retrieve selected address");
        return;
      }
      onAddress1(a.Line1);
      onAddress2(a.Line2);
      onAddress3(a.Line3);
      onCity(a.City);
      onPostcode(a.PostalCode.toUpperCase());
      setFindResults([]);
      setFindError(null);
    } catch (err) {
      setFindError(err instanceof Error ? err.message : "retrieve failed");
    }
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <LabeledInput label="Address 1" value={address1} onChange={onAddress1} />
        <LabeledInput label="Address 2" value={address2} onChange={onAddress2} />
        <LabeledInput label="Address 3" value={address3} onChange={onAddress3} />
        <LabeledInput label="City" value={city} onChange={onCity} />
        <div className="col-span-2 flex items-end gap-2">
          <div className="flex-1">
            <LabeledInput
              label="Postcode"
              value={postcode}
              onChange={(v) => onPostcode(v.toUpperCase())}
            />
          </div>
          <button
            onClick={() => void runFind()}
            disabled={findState === "loading"}
            className="rounded-md border border-ink-500 bg-ink-800 px-3 py-1.5 text-[12px] uppercase tracking-wider text-ink-50 hover:border-slack-aubergineLight disabled:opacity-40"
          >
            {findState === "loading" ? "Searching…" : "Find address"}
          </button>
        </div>
      </div>
      {findError ? (
        <div className="rounded-md border border-slack-pink/30 bg-slack-pink/10 px-2 py-1 text-[13px] text-slack-pink">
          {findError}
        </div>
      ) : null}
      {findResults.length > 0 ? (
        <ul className="max-h-48 overflow-y-auto divide-y divide-ink-700 rounded-md border border-ink-500 bg-ink-900/40 text-[13px]">
          {findResults.map((r) => (
            <li key={r.Id}>
              <button
                onClick={() => void pickResult(r.Id)}
                className="block w-full px-2 py-1.5 text-left text-ink-50 hover:bg-ink-800/60"
              >
                <div className="truncate">{r.Text}</div>
                {r.Description ? (
                  <div className="truncate text-[12px] text-ink-200">
                    {r.Description}
                  </div>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-ink-200">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
      />
    </label>
  );
}

function formatContact(c: AccountContact): string {
  const name = c.fullName || `${c.firstName} ${c.lastName}`.trim() || "(unnamed)";
  const bits: string[] = [name];
  if (c.email) bits.push(c.email);
  const phone = c.phoneMobile || c.phoneHome || c.phoneWork;
  if (phone) bits.push(phone);
  return bits.join(" · ");
}

function formatLocation(l: AccountLocation): string {
  const parts = [l.address1, l.city, l.postcode].filter((s) => s && s.trim());
  // Locations created by the "Create New Account" flow start empty — staff
  // fills them in on the first quote. Show an explicit label so they're
  // still pickable from the dropdown.
  if (parts.length === 0) return `(blank address — Location #${l.lid})`;
  return parts.join(", ");
}
